import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Document {
  id: number
  bg_document_type_id: number | null
  document_type: string | null
  title: string | null
  filename: string | null
  filetype: string | null
  document_date: string | null
  user_name: string | null
  status: number
  has_file: boolean
}

interface Props {
  case_record: CaseRecord
  documents: Document[]
}

export default function Index({ case_record, documents }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Documents", href: `/cases/${case_record.id}/documents` },
  ]

  const basePath = `/cases/${case_record.id}/documents`

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this document?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Documents - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Documents for {case_record.display_name}
          </h1>
          <Link
            href={`${basePath}/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Document
          </Link>
        </div>

        {documents.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Title
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Filename
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Uploaded By
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    {formatDate(doc.document_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`${basePath}/${doc.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {doc.title ?? doc.filename ?? `Document #${doc.id}`}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {doc.filename}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {doc.filetype && (
                      <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-0.5 text-xs">
                        {doc.filetype}
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {doc.user_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    {doc.has_file && (
                      <a
                        href={`${basePath}/${doc.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                      >
                        Download
                      </a>
                    )}
                    <Link
                      href={`${basePath}/${doc.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    <Link
                      href={`${basePath}/${doc.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="text-hpi-red hover:text-hpi-red"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No documents found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
